/**
 * Company: SPHMedia
 * Description: [Fetches recommended articles for myBH]
 */

import { UserToken } from "@app/store/useUserVarStore";
import {
  getArticlesByIds,
  getMoreRecommendedArticleFeeds,
} from "@helper/getRecommendation";
import { useCallback, useEffect, useState } from "react";

import { ProcessedArticleData } from "./transformer/useOSResponse";

type useRecommendedArticlesData = {
  articles: ProcessedArticleData[];
  loading: boolean;
  error: boolean;
  pagination: {
    current: number;
    next: number;
    totalPages: number;
  } | null;
  refetch: () => Promise<void>;
};

type useRecommendedArticlesProps = {
  userToken: UserToken;
  deviceId: string | null;
  page?: number;
  size?: number;
  sections?: string[];
  enabled?: boolean;
  initialArticles?: ProcessedArticleData[]; // Optional initial articles to populate the state
};

/**
 * Custom React Hook: useRecommendedArticlesHook
 * Description: [Fetches recommended articles based on user id or device id for myBH recommended page]
 * @param props - The props required by the hook (if any)
 * @returns data - The data returned by the hook
 */
export default function useRecommendedArticles({
  userToken,
  deviceId,
  page = 1,
  size,
  sections,
  enabled = true,
  initialArticles = [],
}: useRecommendedArticlesProps): useRecommendedArticlesData {
  const [articles, setArticles] =
    useState<ProcessedArticleData[]>(initialArticles);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [pagination, setPagination] =
    useState<useRecommendedArticlesData["pagination"]>(null);

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    setError(false);

    try {
      // Step 1: get IDs
      const { articleIDs, pagination } = await getMoreRecommendedArticleFeeds(
        userToken,
        deviceId,
        page,
        size,
        sections,
      );

      if (articleIDs.length === 0) {
        return;
      }

      // Step 2: get articles
      const articlesData = await getArticlesByIds(articleIDs);

      setArticles(articlesData);
      setPagination(pagination);
    } catch (err) {
      console.error("Failed to fetch recommended articles", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [userToken, deviceId, page, size, sections]);

  // initial + dependency change fetch
  useEffect(() => {
    if (!enabled) return;

    void fetchArticles();
  }, [enabled, fetchArticles]);

  return {
    articles,
    loading,
    error,
    pagination,
    refetch: fetchArticles,
  };
}
